// src/pages/Home.tsx
import React from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '../components/sections/home/Hero';
import FeaturedProducts from '../components/sections/home/FeaturedProducts';
import Mission from '../components/sections/home/Mission';
import Testimonials from '../components/sections/home/Testimonials';
import { 
  History, 
  Leaf, 
  Check, 
  Users 
} from 'lucide-react';

const Home: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Oljarna Krajnc | Tradicija bučnega olja od leta 1927</title>
        <meta 
          name="description" 
          content="Oljarna Krajnc - Tradicionalna pridelava bučnega olja in drugih izdelkov iz bučnih semen že od leta 1927. Odkrijte okus tradicije."
        />
      </Helmet>
      
      <Hero />
      
      <div className="py-12 bg-white">
        <div className="container-wide">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FeatureCard 
              title="Tradicionalni postopki"
              description="Ohranjamo tradicionalne postopke pridelave bučnega olja."
              icon={<History size={36} className="text-primary-600" />}
            />
            
            <FeatureCard 
              title="Lokalne surovine"
              description="Sodelujemo le z lokalnimi pridelovalci bučnih semen."
              icon={<Leaf size={36} className="text-primary-600" />}
            />
            
            <FeatureCard 
              title="Brez dodatkov"
              description="Naši izdelki ne vsebujejo konzervansov ali drugih dodatkov."
              icon={<Check size={36} className="text-primary-600" />}
            />
            
            <FeatureCard 
              title="Družinska tradicija"
              description="Znanje in recepte prenašamo iz generacije v generacijo."
              icon={<Users size={36} className="text-primary-600" />}
            />
          </div>
        </div>
      </div>
      
      <FeaturedProducts />
      
      <div className="bg-white py-16">
        <div className="container-wide">
          <div className="section-title">
            <h2>Zakaj izbrati naše bučno olje?</h2>
            <div className="divider"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <ReasonCard 
              number="01"
              title="Bogat okus"
              description="Naše bučno olje ima intenziven, aromatičen okus, ki obogati vsako jed."
            />
            
            <ReasonCard 
              number="02"
              title="Bogato s hranili"
              description="Vsebuje visoko koncentracijo vitaminov, mineralov in antioksidantov."
            />
            
            <ReasonCard 
              number="03"
              title="Tradicionalna pridelava"
              description="Pridelano po tradicionalnem postopku, ki ohranja vse dragocene sestavine."
            />
          </div>
        </div>
      </div>
      
      <Mission />
      
      <Testimonials />
    </>
  );
};

// Feature card component
const FeatureCard: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
}> = ({ title, description, icon }) => {
  return (
    <div className="bg-primary-50 rounded-lg p-6 hover:shadow-md transition-all duration-300 hover:translate-y-[-5px] flex flex-col items-center text-center">
      <div className="bg-white p-4 rounded-full mb-4 shadow-sm">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-primary-700 mb-2">{title}</h3>
      <p className="text-accent-700">{description}</p>
    </div>
  );
};

// Reason card component
const ReasonCard: React.FC<{
  number: string;
  title: string;
  description: string;
}> = ({ number, title, description }) => {
  return (
    <div className="bg-primary-50 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="text-6xl font-bold text-primary-200 mb-4">
        {number}
      </div>
      <h3 className="text-xl font-bold text-primary-700 mb-2">{title}</h3>
      <p className="text-accent-700">{description}</p>
    </div>
  );
};

export default Home;